import { Box, type BoxProps, Image, SkeletonLoader, Typography } from "@drone-js/core";
import type { FunctionComponent, PropsWithChildren } from "react";
import { AdaptiveBadge, type AdaptiveBadgeProps } from "./AdaptiveBadge";

export interface BasePropertyListingProps extends BoxProps, PropsWithChildren {
    title: string;
    subTitle: string;
    image: string;
    badge?: Omit<AdaptiveBadgeProps, "mobile">;
    childrenPosition?: "before" | "after";
}

export const BasePropertyListing: FunctionComponent<BasePropertyListingProps> = ({
    title,
    subTitle,
    image,
    badge,
    children,
    ...rest
}) => (
    <Box py="8" ps="0" pe="0" borderRadius="4" display="flex" alignItems="center" qM={{ ps: "8", pe: "16" }} {...rest}>
        <Box
            width="64px"
            height="64px"
            flexShrink={0}
            me="16"
            overflow="hidden"
            borderRadius="4"
            position="relative"
            backgroundColor="base.inset"
        >
            <SkeletonLoader label={title} shape="rectangle" width="64px" height="64px" />
            <Box position="absolute" top={0}>
                <Image src={image} alt={title} width="64px" height="64px" borderRadius="4" loading="lazy-blur" />
            </Box>
        </Box>
        <Box>
            {badge && (
                <Box display="block" mb="8" qM={{ display: "none" }}>
                    <AdaptiveBadge mobile {...badge} />
                </Box>
            )}
            <Typography variant="body-default" textColor="base.default">
                {title}
            </Typography>
            <Box display="none" pt="8" qM={{ display: "block" }}>
                <Typography variant="body-s" textColor="base.subtle">
                    {subTitle}
                </Typography>
            </Box>
        </Box>
        <Box mx="auto" />
        <Box display="flex" justifyContent="end" alignItems="center" width="auto" flexShrink={0}>
            {badge && (
                <Box display="none" me="8" qM={{ display: "block" }}>
                    <AdaptiveBadge {...badge} />
                </Box>
            )}
            {children}
        </Box>
    </Box>
);
